// Shared Sentry configuration for JavaScript/React
//
// Architecture:
//   config/sentry.json                          # ← Single source of truth
//       ↓
//   ├─→ lib/sentry_config.rb                    # ← Ruby helper (reads JSON)
//   │   └─→ config/initializers/sentry.rb       # ← Rails Sentry init
//   │
//   ├─→ app/javascript/lib/sentry-config.js     # ← JS helper (imports JSON) [YOU ARE HERE]
//   │   └─→ app/javascript/layout/sentry.js     # ← React Sentry init
//   │
//   └─→ app/views/layouts/application.html.erb  # ← ERB template Sentry init
//
// This file provides helper functions for React to use the centralized config
import sentrySettings from "../../../config/sentry.json"

/**
 * Centralized Sentry configuration loaded from config/sentry.json
 * @typedef {Object} SentrySettings
 * @property {string} dsn - Sentry DSN
 * @property {string[]} deployableEnvironments - Environments where Sentry is enabled
 * @property {boolean} sendDefaultPii - Whether to send PII data
 * @property {{ sampleRate: number }} tracing - Tracing configuration
 * @property {string[]} tracePropagationTargets - Regex patterns for trace propagation
 * @property {{ sessionSampleRate: number, errorSampleRate: number, maskAllText: boolean, maskAllInputs: boolean, blockAllMedia: boolean }} replay - Session replay configuration
 * @property {{ autoTracking: boolean }} session - Session tracking configuration
 * @property {string[]} allowedHosts - Hosts where Sentry is allowed to report
 * @property {string[]} cdnDomains - CDN domains to exclude from span creation
 * @property {string[]} appDomains - App domains to include in span creation
 * @property {string[]} blocklistedPaths - Paths to exclude from span creation
 * @property {{ invalidErrors: string[], networkErrors: string[], sdkNoise: string[], slateErrors: string[] }} errorFilters - Error filtering configuration
 */

/** @type {SentrySettings} */
export const SENTRY_CONFIG = sentrySettings

// Helper to check if current environment should report to Sentry
export function shouldEnableSentry() {
  const isE2ETest = process.env.CI_E2E_TEST === "true"
  const isDeployableEnv = SENTRY_CONFIG.deployableEnvironments.includes(
    process.env.KIT_ENV || process.env.NODE_ENV
  )

  return isDeployableEnv && !isE2ETest
}

// Sentry JS 10+ no longer sets contexts.browser on the client — ingest fills
// that from User-Agent after beforeSend. Read UA from the event (httpContext)
// or navigator so filters can still scope by browser.
function eventUserAgent(event) {
  const headers = event?.request?.headers
  return (
    headers?.["User-Agent"] ||
    headers?.["user-agent"] ||
    (typeof navigator !== "undefined" ? navigator.userAgent : "") ||
    ""
  )
}

function isChromeOrGoogleOnIos(event) {
  return /CriOS\/|GSA\//.test(eventUserAgent(event))
}

function isSafariBrowser(event) {
  const ua = eventUserAgent(event)
  return /Safari\//.test(ua) && !/Chrome\/|CriOS\/|Chromium\/|Edg\/|GSA\//.test(ua)
}

/**
 * Shared error filter logic that can be used by both React and ERB.
 * Returns a reason string (truthy) when the error should be filtered,
 * or false when it should be sent to Sentry. Callers can use the reason
 * to decide whether to log the dropped event (e.g., Slate errors → Kibana).
 *
 * @param {Error} error - The error object
 * @param {string} errorName - The error name/type
 * @param {string} errorValue - The error message
 * @param {Object} [event] - The full Sentry event object
 * @returns {string|false} Reason string if filtered, false otherwise
 */
export function shouldFilterError(error, errorName, errorValue, event = null) {
  // Filter InvalidError with specific messages
  if (errorName === "InvalidError") {
    if (
      SENTRY_CONFIG.errorFilters.invalidErrors.some(msg =>
        errorValue.includes(msg)
      )
    ) {
      return "invalid_error"
    }
  }

  // Filter SDK noise
  if (
    SENTRY_CONFIG.errorFilters.sdkNoise.some(msg => errorValue.includes(msg))
  ) {
    return "sdk_noise"
  }

  // Filter Slate editor errors — known upstream limitation with no fix available.
  // These fire during normal editing when Slate's model and the DOM briefly
  // diverge. The editor error boundary catches crashes and lets users reload.
  // Ref: https://github.com/ianstormtaylor/slate/pull/5407
  if (
    SENTRY_CONFIG.errorFilters.slateErrors?.some(msg =>
      errorValue.includes(msg)
    )
  ) {
    return "slate"
  }

  // Filter Yjs CRDT internal errors during collaborative editing.
  // These are TypeErrors deep inside yjs internals during sync operations
  // (e.g., null access on 'parent', 'left', 'right'). The crash frame is
  // always inside yjs/ with no Kit code on the stack. We match on TypeError +
  // crash frame in yjs/ to stay browser-agnostic (Chrome, Safari, and Firefox
  // all format null-access errors differently).
  if (
    errorName === "TypeError" &&
    event?.exception?.values?.some(v => {
      const frames = v.stacktrace?.frames
      if (!frames?.length) return false
      const crashFrame = frames[frames.length - 1]
      return crashFrame?.filename?.includes("/yjs/")
    })
  ) {
    return "yjs"
  }

  // Filter errors from browser extensions or Safari privacy features
  // webkit-masked-url://hidden indicates masked extension/tracking code, not our app
  if (
    errorValue === "Invalid call to runtime.sendMessage(). Tab not found." ||
    event?.exception?.values?.some(v =>
      v.stacktrace?.frames?.some(f =>
        f.filename?.includes("webkit-masked-url://hidden")
      )
    )
  ) {
    return "browser_extension"
  }

  if (
    errorName === "UnhandledRejection" &&
    errorValue ===
      "Object captured as promise rejection with keys: [object has no keys]" &&
    isSafariBrowser(event) &&
    event?.exception?.values?.some(
      v =>
        v.mechanism?.type?.endsWith("global_handlers.onunhandledrejection") &&
        !v.stacktrace?.frames?.length
    )
  ) {
    return "safari_empty_object_rejection"
  }

  // Chrome and Google on iOS inject minified scripts attributed to the host
  // document. Their errors have no application frames and are not actionable.
  // Message names rotate with each minify pass (fa/ga/pa/ta/…), so match any
  // two-letter token rather than a fixed allowlist.
  if (
    errorName === "Error" &&
    /^[a-z]{2}$/i.test(errorValue) &&
    isChromeOrGoogleOnIos(event) &&
    event?.transaction === "/confirm-subscription" &&
    event?.exception?.values?.some(v => {
      const frames = v.stacktrace?.frames
      return (
        frames?.length === 1 &&
        frames[0].filename?.endsWith("/confirm-subscription")
      )
    })
  ) {
    return "chrome_ios_injected_script"
  }

  // Filter Sentry Session Replay's own cross-origin iframe noise.
  //
  // The Replay integration's onIframeLoad reads contentWindow.Element on every
  // iframe to attach observers. For cross-origin iframes (Stripe, Facebook
  // Pixel, etc.) the browser rightfully throws SecurityError per same-origin
  // policy. The error originates in @sentry-internal/replay, not our code, and
  // recording continues. See KIT-REACT-WX. Known upstream Sentry SDK issues:
  //   - https://github.com/getsentry/sentry-javascript/issues/13173
  //     (Stripe iframe + Replay throwing the exact cross-origin SecurityError)
  //   - https://github.com/getsentry/sentry-javascript/issues/6560
  //     (umbrella issue — long-running, partially fixed across releases)
  //   - https://github.com/getsentry/sentry-javascript/issues/14313
  //     (related contentDocument variant + the rrweb fix pattern)
  //
  // Strict match — drop ONLY when ALL of these are true:
  //   1. SecurityError with the exact "Failed to read a named property
  //      'Element' from 'Window'" prefix (the form Sentry reports for this
  //      group, including both Chromium phrasings — "Blocked a frame ..." and
  //      "An attempt was made to break through the security policy ...").
  //   2. Mechanism type ends with "browserapierrors.addEventListener" (the
  //      Sentry-instrumented variant). The SDK populates this as the full
  //      "auto.browser.browserapierrors.addEventListener" string (see
  //      @sentry/browser/.../integrations/browserapierrors.js), so we suffix-
  //      match to stay tolerant of the SDK shortening or prefixing it in
  //      future versions.
  //   3. The crash frame is inside @sentry-internal/replay.
  // A real same-origin SecurityError originating in our code would have a
  // different mechanism and frames pointing at app/javascript/... and would
  // fall through this filter unchanged.
  if (
    errorName === "SecurityError" &&
    errorValue.startsWith(
      "Failed to read a named property 'Element' from 'Window'"
    ) &&
    event?.exception?.values?.some(v => {
      if (!v.mechanism?.type?.endsWith("browserapierrors.addEventListener"))
        return false
      const frames = v.stacktrace?.frames
      if (!frames?.length) return false
      return frames.some(f => f.filename?.includes("@sentry-internal/replay"))
    })
  ) {
    return "sentry_replay_iframe"
  }

  // Filter the cross-origin "Maximum call stack size exceeded" noise that comes
  // from injected scripts in iOS Chrome / Google iOS app (browser extensions,
  // content blockers, in-app web view bridges). These reach us through
  // window.onerror with a CORS-tainted/anonymous stack, so we cannot attribute
  // them and they are not from our code. See KIT-REACT-8C.
  //
  // History: we originally gated this on `mechanism.handled === false` and
  // `mechanism.type === "onerror"` in addition to the anonymous-stack check.
  // That proved too strict — ~70 events/day kept slipping through because the
  // Sentry SDK does not always populate `mechanism` with those exact shapes
  // for this onerror path (mechanism.type can be the longer form
  // "auto.browser.global_handlers.onerror", or `handled` may be null rather
  // than false). We now rely solely on the stack-trace shape to tell noise
  // apart from a real in-app recursion.
  //
  // Drop ONLY when ALL of the following are true:
  //   1. RangeError with the exact "Maximum call stack size exceeded." message
  //      (the form Sentry reports for this group).
  //   2. No usable stack trace: either `event.exception.values` is missing /
  //      empty, or every value's frames are missing, empty, or entirely
  //      anonymous (no filename, "undefined", or "<anonymous>").
  // A real stack overflow originating in our own code will have proper frames
  // pointing at app/javascript/..., app.kit.com, or cdn.kit.com, and will
  // fall through this filter unchanged — surfacing as a brand-new Sentry
  // issue with a real stack.
  if (
    errorName === "RangeError" &&
    errorValue === "Maximum call stack size exceeded." &&
    (!event?.exception?.values?.length ||
      event.exception.values.every(v => {
        const frames = v.stacktrace?.frames
        if (!frames || frames.length === 0) return true
        return frames.every(
          f =>
            !f.filename ||
            f.filename === "undefined" ||
            f.filename === "<anonymous>"
        )
      }))
  ) {
    return "anonymous_range_error"
  }

  // Filter "Converting circular structure to JSON" when a browser extension
  // monkey-patches appendChild and JSON.stringifies the DOM node React is
  // inserting. React fibers create a circle (element ↔ __reactFiber ↔ stateNode),
  // so stringify throws. The crash frame is always anonymous injected code —
  // react-dom frames appear higher on the stack (commit/appendChild), so we
  // must NOT require an all-anonymous stack. See KIT-REACT-3VM / PLATTY-707.
  //
  // Drop ONLY when ALL of these are true:
  //   1. TypeError whose message includes "Converting circular structure to JSON"
  //   2. Message also mentions __reactFiber (DOM node + React fiber, not our
  //      own circular plain object)
  //   3. Crash frame (last in frames[]) is anonymous
  // A real circular-JSON bug in Kit code would crash inside app/cdn frames.
  if (
    errorName === "TypeError" &&
    errorValue.includes("Converting circular structure to JSON") &&
    errorValue.includes("__reactFiber") &&
    event?.exception?.values?.some(v => {
      const frames = v.stacktrace?.frames
      if (!frames?.length) return false
      const crashFrame = frames[frames.length - 1]
      return (
        !crashFrame?.filename ||
        crashFrame.filename === "undefined" ||
        crashFrame.filename === "<anonymous>"
      )
    })
  ) {
    return "circular_json_dom_extension"
  }

  // Filter dynamic-import chunk-load failures. These come from React.lazy /
  // import() when the user's tab is open across a deploy and the hashed JS
  // chunk it tries to fetch has expired from the CDN. `loadPageComponent`
  // already retries with a hard reload — events that reach Sentry are the
  // ones where reload itself didn't help (chunk genuinely missing, offline,
  // etc.), and aren't actionable.
  if (
    /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|ChunkLoadError/i.test(
      errorValue
    )
  ) {
    return "chunk_load"
  }

  // Expected, and watched by rate instead of per event (KIT-REACT-8S,
  // KIT-REACT-4). Rule:
  // observability/grafana/alerts/rules/websocket-auth/websocket-auth-retry-exhaustion-elevated.yml
  if (/^(Collaboration )?WebSocket auth failed after/.test(errorValue)) {
    return "websocket_auth_retry_exhausted"
  }

  // Filter network errors (from shared config)
  const message = (error?.message || "").toLowerCase()
  if (
    SENTRY_CONFIG.errorFilters.networkErrors.some(pattern =>
      message.includes(pattern.toLowerCase())
    )
  ) {
    return "network"
  }

  return false
}

/**
 * Convert regex patterns from JSON strings to RegExp objects
 * @returns {RegExp[]} Array of RegExp objects for trace propagation targets
 */
export function getTracePropagationTargets() {
  return SENTRY_CONFIG.tracePropagationTargets.map(
    pattern => new RegExp(pattern)
  )
}

/**
 * Build base Sentry config (used by both React and ERB)
 * @param {SentryConfigOptions} [options={}] - Optional config overrides
 * @returns {Pick<import('@sentry/browser').BrowserOptions, 'dsn' | 'sendDefaultPii' | 'release' | 'environment' | 'tracesSampleRate' | 'tracePropagationTargets' | 'replaysSessionSampleRate' | 'replaysOnErrorSampleRate'>} The Sentry configuration options
 */
export function getBaseSentryConfig() {
  return {
    dsn: SENTRY_CONFIG.dsn,
    sendDefaultPii: SENTRY_CONFIG.sendDefaultPii,
    release: process.env.SHORT_CIRCLE_SHA1,
    environment: process.env.KIT_ENV || process.env.NODE_ENV,
    tracePropagationTargets: getTracePropagationTargets(),
    tracesSampleRate: SENTRY_CONFIG.tracing.sampleRate,
    replaysSessionSampleRate: SENTRY_CONFIG.replay.sessionSampleRate,
    replaysOnErrorSampleRate: SENTRY_CONFIG.replay.errorSampleRate,
    // The browser SDK default is 3, which silently coerces anything deeper
    // to the literal string "[object Object]". Our structured diagnostic
    // contexts (e.g. `slate_crash.parent_subtree.children_sample[i].mark_keys`
    // attached by `EditorErrorBoundary`) sit ~6–7 levels deep and were being
    // truncated before ever leaving the browser. Bumping to 10 fixes that
    // without uncapping breadth (default 1000 is plenty for our payloads).
    normalizeDepth: 10
  }
}
